import React from "react";
import styled from "styled-components";
import { portalColors } from "./BusinessPortalComponents";
import StyledSwitch from "./StyledSwitch";
import {
    StandardModalState,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection
} from "./StandardModal";
import { generateNameInitials } from "src/utils/helpers";

export const UserSelectionSummaryBar = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

export const UserSelectionSummaryLabel = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    line-height: 1.3;
`;

export const UserSelectionChip = styled.div`
    min-width: 0;
    min-height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.45rem 0.65rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surface};
    box-sizing: border-box;
`;

export const UserSelectionChipBody = styled.div`
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
`;

export const UserSelectionChipIcon = styled.div`
    width: 2rem;
    height: 2rem;
    flex: 0 0 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ffffff;
    border-radius: 999px;
    background: #d6d6d6;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);

    img {
        width: 1rem;
        height: 1rem;
        object-fit: contain;
    }
`;

export const UserSelectionChipText = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

export const UserSelectionRemoveButton = styled.button`
    width: 1.75rem;
    height: 1.75rem;
    flex: 0 0 1.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;
    padding: 0;

    img {
        width: 1.375rem;
        height: 1.375rem;
        object-fit: contain;
    }
`;

export const UserSelectionControls = styled.div`
    display: grid;
    grid-template-columns: minmax(14rem, 1fr) minmax(13rem, auto);
    gap: 0.85rem;
    align-items: end;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const UserSelectionFieldGroup = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.45rem;
`;

export const UserSelectionLabel = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    line-height: 1.35;
`;

export const UserSelectionSearchInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 2.75rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-sizing: border-box;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0 2.6rem 0 1rem;
    outline: none;

    ::placeholder {
        color: ${portalColors.textSoft};
        font-weight: 500;
    }

    &:focus,
    &:focus-visible {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const UserSelectionCount = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;
    line-height: 1.35;
    text-align: right;

    @media (max-width: 760px) {
        text-align: left;
    }
`;

export const UserSelectionToggleBox = styled.label`
    min-height: 2.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-sizing: border-box;
    cursor: pointer;
    padding: 0 0.45rem 0 0.9rem;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.35;
`;

export const UserSelectionNameCell = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.65rem;
`;

export const UserSelectionAvatar = styled.div`
    width: ${p => p.size || "2.35rem"};
    height: ${p => p.size || "2.35rem"};
    flex: 0 0 ${p => p.size || "2.35rem"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ffffff;
    border-radius: 999px;
    background: #d6d6d6;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.8125rem;
    font-weight: 800;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export const UserSelectionCellText = styled.div`
    min-width: 0;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: ${p => p.weight || 600};
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

export const UserSelectionSelectButton = styled.button`
    width: 1.625rem;
    height: 1.625rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #707070;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;
    padding: 0;
`;

export const UserSelectionCheckButton = styled.button`
    width: 1.625rem;
    height: 1.625rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    cursor: pointer;
    padding: 0;

    img {
        width: 1.625rem;
        height: 1.625rem;
        object-fit: contain;
    }
`;

export const UserSelectionTable = styled.div`
    tr.is-selected td {
        background: ${portalColors.blueSoft};
    }

    td:last-child,
    th:last-child {
        text-align: center;
    }
`;

export const UserSelectionMetricSummary = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: end;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

export const UserSelectionMetricCopy = styled.div`
    min-width: 0;
    display: grid;
    gap: 0.45rem;
`;

export const UserSelectionMetricTitle = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.35;
`;

export const UserSelectionMetricDescription = styled.div`
    color: ${portalColors.mutedText};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.5;
    max-width: 45rem;
`;

export const UserSelectionMetricValue = styled.div`
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.05;
    text-align: right;
    white-space: nowrap;

    @media (max-width: 720px) {
        text-align: left;
        white-space: normal;
    }
`;

const getViewerName = (viewer) => {
    if (viewer.employeeName) {
        return viewer.employeeName;
    }

    return [viewer.firstName, viewer.lastName].filter(Boolean).join(" ");
};

const getViewerInitials = (viewer, fullName) => {
    if (viewer.firstName || viewer.lastName) {
        return generateNameInitials(viewer.firstName, viewer.lastName);
    }

    const nameParts = fullName.split(" ").filter(Boolean);
    return generateNameInitials(nameParts[0], nameParts[nameParts.length - 1]);
};

export function UserViewerListPreview({
    viewersList,
    isReaderTypeEmployee,
    emptyMessage = "No users found",
    nameHeader,
    employeeSecondaryHeader = "Department / Group",
    employeeTertiaryHeader = "Job Title",
    clientSecondaryHeader = "Job Title",
    maxHeight = "min(30rem, calc(100vh - 27rem))"
}) {
    if (!viewersList || viewersList.length === 0) {
        return (
            <StandardModalTableSection>
                <StandardModalState minHeight="8rem" border="0">{emptyMessage}</StandardModalState>
            </StandardModalTableSection>
        );
    }

    const resolvedNameHeader = nameHeader || (isReaderTypeEmployee ? "Employee Name" : "Client Name");
    const minimumWidth = isReaderTypeEmployee ? "760px" : "560px";

    return (
        <StandardModalTableSection>
            <StandardModalTableScroll maxHeight={maxHeight}>
                <UserSelectionTable>
                    <StandardModalTable minWidth={minimumWidth}>
                        <thead>
                            <tr>
                                <th style={{ width: isReaderTypeEmployee ? "40%" : "55%" }}>{resolvedNameHeader}</th>
                                <th style={{ width: isReaderTypeEmployee ? "30%" : "45%" }}>
                                    {isReaderTypeEmployee ? employeeSecondaryHeader : clientSecondaryHeader}
                                </th>
                                {isReaderTypeEmployee && (
                                    <th style={{ width: "30%" }}>{employeeTertiaryHeader}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {viewersList.map((viewer, index) => {
                                const fullName = getViewerName(viewer);
                                const nameInitials = getViewerInitials(viewer, fullName);
                                const secondaryValue = isReaderTypeEmployee
                                    ? viewer.department
                                    : viewer.employmentPosition || viewer.position;
                                const tertiaryValue = viewer.employmentPosition || viewer.position;

                                return (
                                    <tr key={viewer.uuid || viewer.employeeUuid || `${fullName}-${index}`}>
                                        <td>
                                            <UserSelectionNameCell>
                                                <UserSelectionAvatar>{nameInitials}</UserSelectionAvatar>
                                                <UserSelectionCellText>{fullName || "-"}</UserSelectionCellText>
                                            </UserSelectionNameCell>
                                        </td>
                                        <td>{secondaryValue || "-"}</td>
                                        {isReaderTypeEmployee && <td>{tertiaryValue || "-"}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </StandardModalTable>
                </UserSelectionTable>
            </StandardModalTableScroll>
        </StandardModalTableSection>
    );
}

export const UserSelectionSwitch = (props) => (
    <StyledSwitch
        switchWidth={41}
        switchHeight={25}
        switchMargin={8}
        checkedTranslateX={16}
        checkedTrackColor="#171A22"
        focusColor="#171A22"
        thumbSize={20}
        thumbBorderColor="#bdbdbd"
        trackHeight={22}
        trackRadius={26 / 2}
        {...props}
    />
);
